import React, { useReducer, useEffect, useState, useRef, useMemo, useCallback } from 'react';

import PropTypes from 'prop-types';

import { isEmpty } from 'lodash';
import { FormattedMessage, useIntl } from 'react-intl';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import useMediaQuery from '@material-ui/core/useMediaQuery';

import { useTheme } from '@material-ui/core/styles';

import { get } from 'utils/lodash';
import { formatTacSubmitData } from 'utils/self-unlock';
import { isLengthValid, isPasswordMatchSelfUnlock as checkPasswordMatch, isValidPassword } from 'utils/validation';
import {
  postUnlockUser,
  postUsernameToEmail,
  resetSelfUnlockState,
  postResetPasswordTacSubmit,
  getVerifySelfUnlockDetails,
  postResetPasswordTacRequest,
  putResetPasswordTacSubmitResponse,
  putResetPasswordTacRequestResponse,
} from 'middleware/actions/self-unlock';

import { BasicDrawer, Secure2uDrawer, TacDrawer } from 'components/common/drawers';
import { FormModal } from 'components/common/modals';
import { AccountForm, CreditCardForm, ResetUsingForm, UnlockForm, ResetPasswordForm, SelfUnlockFooter } from 'components/self-unlock';

import { TAC_STEP_FINAL, TAC_STEP_INITIAL, TAC_STEP_RETRY } from 'settings/constants/tac';
import { DRAWER_TYPE_ERROR, SIDEBAR_VARIANT } from 'settings/constants/ui-control';
import { REJECT_TRANSACTION_SECURE2U_RESPONSE_CODE, SUCCESS_RESPONSE_CODE, TAC_EXCEED_MAX_TRIES } from 'settings/constants/response-codes';
import {
  ACCOUNT_NUMBER_MAX_LENGTH,
  checkSecure2uFalse,
  checkSecure2uTrue,
  CREDIT_CARD_MAX_LENGTH,
  EXPIRY_MAX_LENGTH,
  NEW_CREDIT_CARD_MIN_LENGTH,
  PIN_MAX_LENGTH,
} from 'settings/constants/common';
import {
  CREDIT_CARD_CODE,
  CASA_ACCOUNT_CODE,
  USER_STATUS_NORMAL,
  USER_STATUS_LOCKED,
  USER_STATUS_ENABLED,
  USER_STATUS_DISABLED,
  DEFAULT_ACTION_VALUE,
  USER_STATUS_TAC_LOCK,
  ACTION_FORGOT_USER_ID,
  ACTION_RESET_PASSWORD,
} from 'settings/constants/self-unlock';
import { DEFAULT_CURRENCY_KEY } from 'settings/constants/registration';
import moment from 'moment';
import {
  getActivationSec2uPrelogin,
  getCheckCoolingOffPreLogin,
  getPoolingSecure2uPrelogin,
  getPushContentSecure2uPrelogin,
  putActivationSec2uPrelogin,
  putCheckCoolingOffPreLogin,
} from '../../middleware/actions/sec2u';
import { DynoAlertModal } from '../dyno-template/modals';
import { generateSHA256Hash } from '../../utils/crypto';
import { putErrorResponse } from '../../middleware/actions/error';

const SelfUnlockModal = ({
  isOpen,
  onChange,
  clearDrawer,
  setDrawer,
  verifiedDetail,
  resetPasswordTacRequest,
  resetPasswordTacSubmit,
  handleCloseCoachmark,
  handleTutorial,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const fullScreen = useMediaQuery(theme.breakpoints.down('xs'));
  const [isPolling, setIsPolling] = useState(false);
  const [backRetrySecure2u, setBackRetrySecure2u] = useState(false);
  const [openDrawerErr, setOpenDrawerErr] = useState(false);
  const [openDrawerErrCheckUser, setOpenDrawerErrCheckUser] = useState(false);
  const [openDrawerErrCheckCollingOff, setOpenDrawerErrCheckCollingOff] = useState(false);
  const [isPasswordMatch, setPasswordMatch] = useState(false);
  const prevIsTacRequestSuccess = useRef(false);
  const hasDispatchedRef = useRef(false);

  const { activationSec2uPrelogin, pushContentSecure2uPrelogin, poolingSecure2uPrelogin, checkCoolingOffPreLogin } = useSelector(
    stateRedux => ({
      activationSec2uPrelogin: stateRedux.Sec2uReducer.activationSec2uPrelogin,
      pushContentSecure2uPrelogin: stateRedux.Sec2uReducer.pushContentSecure2uPrelogin,
      poolingSecure2uPrelogin: stateRedux.Sec2uReducer.poolingSecure2uPrelogin,
      checkCoolingOffPreLogin: stateRedux.Sec2uReducer.checkCoolingOffPreLogin,
    }),
    shallowEqual
  );

  const { formatMessage } = useIntl();
  const { TEMPORARY } = SIDEBAR_VARIANT;

  const [state, setState] = useReducer((prevState, newState) => ({ ...prevState, ...newState }), {
    pin: '',
    expiryYear: '',
    expiryMonth: '',
    newPassword: '',
    accountNumber: '',
    creditCardNumber: '',
    selectedCurrency: DEFAULT_CURRENCY_KEY,
    isTacDrawerOpen: false,
    method: CASA_ACCOUNT_CODE,
    tacStep: TAC_STEP_INITIAL,
    isResetPasswordForm: false,
    activeSecure2u: false,
    newPasswordConfirmation: '',
    selectedAction: DEFAULT_ACTION_VALUE,
  });

  const {
    pin,
    method,
    tacStep,
    expiryYear,
    expiryMonth,
    newPassword,
    accountNumber,
    selectedAction,
    isTacDrawerOpen,
    creditCardNumber,
    selectedCurrency,
    isResetPasswordForm,
    activeSecure2u,
    newPasswordConfirmation,
  } = state;

  const userStatus = get(verifiedDetail, 'userStatus', null);
  const encryptedUserId = get(verifiedDetail, 'encryptedUserId', null);

  const isCasaAccountCode = method === CASA_ACCOUNT_CODE;
  const isCreditCardCode = method === CREDIT_CARD_CODE;

  const isStatusNormal = userStatus === USER_STATUS_NORMAL;
  const isStatusEnabled = userStatus === USER_STATUS_ENABLED;
  const isStatusDisabled = userStatus === USER_STATUS_DISABLED;
  const isStatusLocked = userStatus === USER_STATUS_LOCKED;
  const isStatusTacLocked = userStatus === USER_STATUS_TAC_LOCK;

  // TAC
  const isTacCodeRequested = !isEmpty(resetPasswordTacRequest);
  const isTacCodeSubmitted = !isEmpty(resetPasswordTacSubmit);
  const isTacRequestSuccess = get(resetPasswordTacRequest, 'isSuccess', '');
  const tacSentTime = get(resetPasswordTacRequest, 'tacSentTime', '');
  const restSMSService = get(resetPasswordTacRequest, 'restSMSService', {});
  const isTacSubmitSuccess = get(resetPasswordTacSubmit, 'isSuccess', '');
  const tacSubmitResponseCode = get(resetPasswordTacSubmit, 'responseCode', '');

  const { responseCode: responsePushContent } = pushContentSecure2uPrelogin;

  const isPasswordEmpty = () => isEmpty(newPassword) || isEmpty(newPasswordConfirmation);
  const isPasswordEqual = () => newPassword === newPasswordConfirmation;
  const tacSentTimeNew = `${moment().utcOffset('+07:00').format('DD MMM YYYY HH:mm:ss')} GMT+7`;
  const { isActive } = activationSec2uPrelogin;
  const { isCoolingOff } = checkCoolingOffPreLogin;

  const handleChange = (name, value) => setState({ [name]: value });

  const showPopup = () => {
    if (window.innerWidth > 769) handleTutorial(false, true, false);
    if (window.innerWidth < 769) handleTutorial(false, true, false);
  };

  const clearModalState = () => {
    dispatch(resetSelfUnlockState());
    setState({
      newPassword: '',
      method: CASA_ACCOUNT_CODE,
      isResetPasswordForm: false,
      newPasswordConfirmation: '',
      selectedAction: DEFAULT_ACTION_VALUE,
    });
  };

  const handleBack = () => clearModalState();

  const closeModal = (event, reason) => {
    dispatch(putActivationSec2uPrelogin({}));
    if (reason === 'backdropClick') return;
    clearModalState();
    onChange(false);
  };

  const handleAccount = () => {
    if (!selectedCurrency) {
      setDrawer({ id: 'selfUnlock.errorMessage.currency' }, DRAWER_TYPE_ERROR);
      return false;
    }

    if (!isLengthValid(accountNumber, ACCOUNT_NUMBER_MAX_LENGTH)) {
      setDrawer({ id: 'selfUnlock.errorMessage.accountLength' }, DRAWER_TYPE_ERROR);
      return false;
    }

    if (!isLengthValid(pin, PIN_MAX_LENGTH)) {
      setDrawer({ id: 'selfUnlock.errorMessage.pinLength' }, DRAWER_TYPE_ERROR);
      return false;
    }
    return true;
  };

  const handleCreditCard = () => {
    if (!isLengthValid(creditCardNumber, NEW_CREDIT_CARD_MIN_LENGTH, CREDIT_CARD_MAX_LENGTH)) {
      setDrawer({ id: 'selfUnlock.errorMessage.ccLength' }, DRAWER_TYPE_ERROR);
      return false;
    }

    if (!isLengthValid(pin, PIN_MAX_LENGTH)) {
      setDrawer({ id: 'selfUnlock.errorMessage.pinLength' }, DRAWER_TYPE_ERROR);
      return false;
    }

    if (!isLengthValid(expiryMonth, EXPIRY_MAX_LENGTH)) {
      setDrawer({ id: 'selfUnlock.errorMessage.ccMmLength' }, DRAWER_TYPE_ERROR);
      return false;
    }

    if (!isLengthValid(expiryYear, EXPIRY_MAX_LENGTH)) {
      setDrawer({ id: 'selfUnlock.errorMessage.ccYyLength' }, DRAWER_TYPE_ERROR);
      return false;
    }
    return true;
  };
  const localStorageLanguage = isEmpty(localStorage.getItem('language')) ? 'id' : localStorage.getItem('language').slice(1, -1);

  const handleCloseDrawerErr = () => {
    setOpenDrawerErr(false);
    setOpenDrawerErrCheckUser(false);
    setBackRetrySecure2u(false);

    Object.keys(pushContentSecure2uPrelogin).forEach(key => delete pushContentSecure2uPrelogin[key]);
    Object.keys(poolingSecure2uPrelogin).forEach(key => delete poolingSecure2uPrelogin[key]);
    // eslint-disable-next-line no-param-reassign
    // Object.keys(tacDetail).forEach(key => delete tacDetail[key]);
    Object.keys(activationSec2uPrelogin).forEach(key => delete activationSec2uPrelogin[key]);

    setState({
      tacStep: TAC_STEP_INITIAL,
      isTacDrawerOpen: true,
    });
  };

  const handleCloseDrawerErrCoolingOff = () => {
    setOpenDrawerErrCheckCollingOff(false);
    dispatch(putCheckCoolingOffPreLogin({}));
  };

  const handleCheckCoolingOff = () => dispatch(getCheckCoolingOffPreLogin({ encryptedUserId }));
  const handleRequestTac = () => {
    setState({ tacStep: TAC_STEP_INITIAL });
    setState({ isTacDrawerOpen: true });
    // setState({ tacStep: TAC_STEP_INITIAL });
    // // clear out response before calling TAC request again
    // dispatch(putResetPasswordTacRequestResponse({}));
    //
    // // If passed validation, call TAC request
    // dispatch(postResetPasswordTacRequest());
  };
  const handleSecure2u = () => {
    // clear out response before calling TAC request again
    dispatch(putResetPasswordTacRequestResponse({}));

    // If passed validation, call TAC request
    dispatch(postResetPasswordTacRequest());
  };

  const retrySecure2uHandler = () => handleSecure2u();

  const verifySelfUnlockDetails = () => {
    if (method === CASA_ACCOUNT_CODE && !handleAccount()) return;
    if (method === CREDIT_CARD_CODE && !handleCreditCard()) return;

    dispatch(getVerifySelfUnlockDetails(state));
  };

  const handleSubmit = () => {
    if (!userStatus) verifySelfUnlockDetails();

    // NOTE: Handle submit for userStatus normal and Enabled
    if (isStatusNormal || isStatusEnabled) {
      if (selectedAction === ACTION_FORGOT_USER_ID) {
        closeModal();
        dispatch(postUsernameToEmail(encryptedUserId));
      }

      if (selectedAction === ACTION_RESET_PASSWORD) {
        // NOTE: dispatch for resetPassword
        if (!isResetPasswordForm) {
          // handleChange('isResetPasswordForm', true);
          dispatch(getActivationSec2uPrelogin({ encryptedUserId }));
          return;
        }

        if (isPasswordEmpty()) {
          setDrawer({ id: 'selfUnlock.errorMessage.noPassword' }, DRAWER_TYPE_ERROR);
          return;
        }

        if (!isPasswordEqual()) {
          setDrawer({ id: 'selfUnlock.errorMessage.passwordNotMatch' }, DRAWER_TYPE_ERROR);
          setState({ newPassword: '', newPasswordConfirmation: '' });
          return;
        }

        if (!isValidPassword(newPassword)) {
          setDrawer({ id: 'selfUnlock.errorMessage.invalidPassword' }, DRAWER_TYPE_ERROR);
          setState({ newPassword: '', newPasswordConfirmation: '' });
          return;
        }

        handleCheckCoolingOff({ encryptedUserId });
      }
    }

    if (isStatusLocked || isStatusTacLocked) {
      closeModal();
      dispatch(postUnlockUser({ userStatus, encryptedUserId }));
    }

    if (isStatusDisabled) closeModal();
  };

  const memoizedProps = useMemo(
    () => ({
      isTacRequestSuccess,
      responsePushContent,
    }),
    [isTacRequestSuccess, responsePushContent]
  );

  const handleDispatch = useCallback(() => {
    dispatch(
      getPushContentSecure2uPrelogin({
        encryptedUserId,
        transactionType: localStorageLanguage === 'id' ? 'Atur Ulang Kata Sandi' : 'Reset Password',
        amount: '',
      })
    );
  }, [dispatch, verifiedDetail]);

  const handleSubmitTac = tacValue => {
    const tacSubmitPayload = formatTacSubmitData({ tacValue, newPassword, encryptedUserId, restSMSService });

    // clear tac response code before resubmit tac again
    dispatch(putResetPasswordTacSubmitResponse({}));
    dispatch(postResetPasswordTacSubmit(tacSubmitPayload));
  };

  const description = () => {
    if (isResetPasswordForm) return { id: 'selfUnlock.modal.header.resetPasswordDescription' };
    return userStatus ? { id: 'selfUnlock.modal.header.activityDescription' } : { id: 'selfUnlock.modal.header.description' };
  };

  const handleCloseModalNotActiveSecure2u = () => {
    dispatch(putActivationSec2uPrelogin({}));
    setState({ ...state, activeSecure2u: false });
  };

  useEffect(() => {
    Object.keys(pushContentSecure2uPrelogin).forEach(key => delete pushContentSecure2uPrelogin[key]);
    Object.keys(poolingSecure2uPrelogin).forEach(key => delete poolingSecure2uPrelogin[key]);
    Object.assign(activationSec2uPrelogin, {});
  }, []);

  useEffect(() => {
    if (!isEmpty(isCoolingOff) && isCoolingOff === generateSHA256Hash(`${encryptedUserId}${checkSecure2uTrue}`)) {
      handleRequestTac();
      dispatch(putCheckCoolingOffPreLogin({}));
    }

    if (!isEmpty(isCoolingOff) && isCoolingOff === generateSHA256Hash(`${encryptedUserId}${checkSecure2uFalse}`)) {
      setOpenDrawerErrCheckCollingOff(true);
      dispatch(putCheckCoolingOffPreLogin({}));
    }
  }, [isCoolingOff]);

  useEffect(() => {
    setPasswordMatch(checkPasswordMatch(newPassword, newPasswordConfirmation));
  }, [newPassword, newPasswordConfirmation]);

  useEffect(() => {
    const shouldDispatch = isTacRequestSuccess && !prevIsTacRequestSuccess.current && !hasDispatchedRef.current;

    if (shouldDispatch) {
      handleDispatch();
      hasDispatchedRef.current = true;
    }

    prevIsTacRequestSuccess.current = isTacRequestSuccess;

    if (isTacRequestSuccess && responsePushContent === SUCCESS_RESPONSE_CODE) {
      setState({
        timestamp: tacSentTime,
        tacStep: TAC_STEP_FINAL,
        isLeavePageBlocked: true,
      });
      hasDispatchedRef.current = false; // Reset flag dispatched
    }
  }, [memoizedProps, handleDispatch]);

  // useEffect(() => {
  //   if (!isTacCodeRequested) return;
  //   // If TAC request response have error, display an error drawer
  //   if (!isTacRequestSuccess) setDrawer({ id: 'errorMessages.ERR_REQUEST_TIMEOUT' }, DRAWER_TYPE_ERROR);
  //   // If TAC request is success, open Tac drawer
  //   if (isTacRequestSuccess) setState({ isTacDrawerOpen: true, tacStep: TAC_STEP_FINAL });
  // }, [isTacRequestSuccess]);

  useEffect(() => {
    if (tacStep === TAC_STEP_RETRY) handleCloseCoachmark();

    if (tacStep === TAC_STEP_FINAL) {
      setBackRetrySecure2u(false);
      setIsPolling(true);
    }
    if (tacStep === TAC_STEP_INITIAL || tacStep === TAC_STEP_RETRY) {
      // eslint-disable-next-line no-param-reassign
      // Object.keys(tacDetail).forEach(key => delete tacDetail[key]);
      setIsPolling(false);
    }
  }, [tacStep]);

  useEffect(() => {
    if (!isTacCodeSubmitted) return;
    // If tac submitted but not success, reopen tac drawer
    if (tacSubmitResponseCode !== SUCCESS_RESPONSE_CODE) {
      setState({ tacStep: TAC_STEP_INITIAL, isTacDrawerOpen: false });
      Object.keys(pushContentSecure2uPrelogin).forEach(key => delete pushContentSecure2uPrelogin[key]);
      Object.keys(poolingSecure2uPrelogin).forEach(key => delete poolingSecure2uPrelogin[key]);
      Object.assign(activationSec2uPrelogin, {});
    }
    // If tac submit success OR tac has been locked, close modal and tac drawer
    if (isTacSubmitSuccess || tacSubmitResponseCode === TAC_EXCEED_MAX_TRIES) {
      closeModal();
      setBackRetrySecure2u(false);
      setState({ tacStep: TAC_STEP_INITIAL, isTacDrawerOpen: false });
      Object.keys(pushContentSecure2uPrelogin).forEach(key => delete pushContentSecure2uPrelogin[key]);
      Object.keys(poolingSecure2uPrelogin).forEach(key => delete poolingSecure2uPrelogin[key]);
      Object.assign(activationSec2uPrelogin, {});
    }
  }, [isTacSubmitSuccess]);

  useEffect(() => {
    if (isActive !== generateSHA256Hash(`${encryptedUserId}${checkSecure2uTrue}`) && !isEmpty(activationSec2uPrelogin)) {
      clearModalState();
      onChange(false);
      setState({ activeSecure2u: true });
    }

    if (isActive === generateSHA256Hash(`${encryptedUserId}${checkSecure2uTrue}`) && !isEmpty(activationSec2uPrelogin)) {
      handleChange('isResetPasswordForm', true);
    }
  }, [activationSec2uPrelogin]);

  useEffect(() => {
    const { securityHash } = pushContentSecure2uPrelogin;
    const { responseCode } = poolingSecure2uPrelogin;

    if (responseCode === SUCCESS_RESPONSE_CODE) {
      setIsPolling(false);
      Object.keys(pushContentSecure2uPrelogin).forEach(key => delete pushContentSecure2uPrelogin[key]);
      Object.keys(poolingSecure2uPrelogin).forEach(key => delete poolingSecure2uPrelogin[key]);

      handleSubmitTac('818888888');
      setState({ tacStep: TAC_STEP_INITIAL, isTacDrawerOpen: false });

      handleCloseCoachmark();
      dispatch(putErrorResponse({}));
    }

    if (responseCode === REJECT_TRANSACTION_SECURE2U_RESPONSE_CODE) {
      handleCloseCoachmark();

      setOpenDrawerErr(true);
      setBackRetrySecure2u(false);
      setState({ isTacDrawerOpen: false });
      setState({ tacStep: TAC_STEP_INITIAL });
      Object.keys(pushContentSecure2uPrelogin).forEach(key => delete pushContentSecure2uPrelogin[key]);
      Object.keys(poolingSecure2uPrelogin).forEach(key => delete poolingSecure2uPrelogin[key]);
      // eslint-disable-next-line no-param-reassign
      // Object.keys(tacDetail).forEach(key => delete tacDetail[key]);
      setIsPolling(false);
    }

    let intervalId; // Declare intervalId outside the if block

    if (isPolling) {
      // Define the function to be called every 3 seconds
      const poolingDataSecure2u = () => {
        dispatch(
          getPoolingSecure2uPrelogin({
            encryptedUserId,
            securityHash,
          })
        );
      };

      // Set up the interval to call poolingDataSecure2u every 3 seconds
      intervalId = setInterval(poolingDataSecure2u, 3000);
    }

    // Cleanup function to clear the interval
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isPolling, poolingSecure2uPrelogin, dispatch, encryptedUserId, pushContentSecure2uPrelogin]);

  return (
    <>
      <FormModal
        fullWidth
        isOpen={isOpen}
        fullScreen={fullScreen}
        maxWidth="sm"
        title={formatMessage({ id: 'selfUnlock.modal.title' })}
        contentTitle={formatMessage({ id: 'selfUnlock.modal.header.title' })}
        contentSubtitle={formatMessage({ id: 'selfUnlock.modal.header.subtitle' })}
        description={formatMessage(description())}
        onClose={closeModal}
        footer={
          <SelfUnlockFooter
            handleSubmit={handleSubmit}
            verifiedDetail={verifiedDetail}
            isResetPasswordForm={isResetPasswordForm}
            handleBack={handleBack}
            isPasswordMatch={isPasswordMatch}
          />
        }
      >
        <form noValidate className="ph4 pv3">
          {isResetPasswordForm ? (
            <ResetPasswordForm newPassword={newPassword} newPasswordConfirmation={newPasswordConfirmation} handleChange={handleChange} />
          ) : (
            <>
              {userStatus ? (
                <UnlockForm verifiedDetail={verifiedDetail} selectedAction={selectedAction} handleChange={handleChange} />
              ) : (
                <ResetUsingForm handleChange={handleChange} method={method} />
              )}

              {isCasaAccountCode && !userStatus && (
                <AccountForm
                  pin={pin}
                  accountNumber={accountNumber}
                  selectedCurrency={selectedCurrency}
                  handleChange={handleChange}
                  clearDrawer={clearDrawer}
                  setDrawer={setDrawer}
                />
              )}

              {isCreditCardCode && !userStatus && (
                <CreditCardForm
                  pin={pin}
                  creditCardNumber={creditCardNumber}
                  expiryMonth={expiryMonth}
                  expiryYear={expiryYear}
                  handleChange={handleChange}
                  clearDrawer={clearDrawer}
                  setDrawer={setDrawer}
                />
              )}
            </>
          )}
        </form>
      </FormModal>

      {/* <TacDrawer */}
      {/*  variant={TEMPORARY} */}
      {/*  currentStep={tacStep} */}
      {/*  timestamp={tacSentTime} */}
      {/*  isOpen={isTacDrawerOpen} */}
      {/*  onRequestTac={handleRequestTac} */}
      {/*  onClose={() => handleChange('isTacDrawerOpen', !isTacDrawerOpen)} */}
      {/*  onSubmitTac={tacValue => handleSubmitTac(tacValue)} */}
      {/*  title={formatMessage({ id: 'selfUnlock.resetPassword.tacTitle' })} */}
      {/*  description={formatMessage({ id: 'selfUnlock.resetPassword.tacDescription' })} */}
      {/* /> */}

      <Secure2uDrawer
        currentStep={tacStep}
        timestamp={tacSentTimeNew}
        isOpen={isTacDrawerOpen}
        onRequestSecure2u={handleSecure2u}
        onSubmitTac={handleSubmitTac}
        onRetrySecure2u={retrySecure2uHandler}
        showPopup={showPopup}
        title={formatMessage({ id: 'transaction.transfer.tacTitle' })}
        onChange={setState}
        handleCloseCoachmark={handleCloseCoachmark}
        backRetrySecure2u={backRetrySecure2u}
        prelogin
      />

      <>
        <DynoAlertModal
          isOpen={activeSecure2u}
          onClose={handleCloseModalNotActiveSecure2u}
          onCancel={() => window.open('https://www.maybank.co.id/ebanking/how-to/Other/lupa-password', '_blank')}
          onSubmit={handleCloseModalNotActiveSecure2u}
          hasCancelButton
          title={formatMessage({ id: 'secure2u.titleNotActive' })}
          description={formatMessage({ id: 'secure2u.descNotActive' })}
          actionButtonTitle={formatMessage({ id: 'secure2u.ok' })}
          secondaryActionButtonTitle={formatMessage({ id: 'secure2u.buttonHowTo' })}
          hasCheckbox={false}
        />

        <BasicDrawer className="error" isOpen={openDrawerErr || openDrawerErrCheckUser} hideBackdrop={false} onClose={handleCloseDrawerErr}>
          <h1 className="f5 lh-copy tc white">
            {openDrawerErrCheckUser && <FormattedMessage id="errorMessages.ERR_SERVER_ERROR" />}
            {openDrawerErr && <FormattedMessage id="errorMessages.ERR_SECURE2U" />}
          </h1>
        </BasicDrawer>

        <BasicDrawer className="error" isOpen={openDrawerErrCheckCollingOff} hideBackdrop={false} onClose={handleCloseDrawerErrCoolingOff}>
          <h1 className="f5 lh-copy tc white">
            {openDrawerErrCheckCollingOff && <FormattedMessage id="errorMessages.ERR_CHECK_COOLING_OFF" />}
          </h1>
        </BasicDrawer>
      </>
    </>
  );
};

SelfUnlockModal.defaultProps = {
  resetPasswordTacRequest: {},
  resetPasswordTacSubmit: {},
  handleCloseCoachmark: null,
  handleTutorial: null,
};

SelfUnlockModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  setDrawer: PropTypes.func.isRequired,
  clearDrawer: PropTypes.func.isRequired,
  verifiedDetail: PropTypes.object.isRequired,
  resetPasswordTacRequest: PropTypes.object,
  resetPasswordTacSubmit: PropTypes.object,
  handleCloseCoachmark: PropTypes.func,
  handleTutorial: PropTypes.func,
};

export default SelfUnlockModal;
